import { getLandingVertical, getPreviewVertical } from "@services/verticals";
import {
  isRhcfqhcOrGenericFacilityType,
  resolveSnfCheckoutOverride,
} from "@utils/snfCheckoutType";
import { resolveRhcFacilityTypeOverride } from "@utils/rhcFacilityType";

export const PREFERRED_FACILITY_FOR_PRICING_KEY = "preferred_facility_for_pricing";

export type PricingRouteState = {
  fromPreferredFacilityId?: unknown;
};

export function toFiniteNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

export function getRoutePreferredFacilityId(
  routeState: PricingRouteState | null | undefined,
): number | null {
  return toFiniteNumber(routeState?.fromPreferredFacilityId);
}

/** Vertical on a stored preferred_facility_for_pricing blob; legacy → healthcare. */
export function storedPreferredFacilityVertical(
  parsed: Record<string, unknown> | null | undefined,
): string {
  if (!parsed) return "healthcare";
  const details =
    (parsed.facility_details as Record<string, unknown> | null) || null;
  const raw = parsed.vertical ?? details?.vertical;
  if (typeof raw === "string" && raw.trim()) {
    return raw.trim().toLowerCase();
  }
  return "healthcare";
}

/** Preview vertical, else landing auth vertical (e.g. /aviation login). */
export function resolvePreferredPricingVerticalScope(
  explicit?: string | null,
): string | null {
  if (explicit != null && String(explicit).trim()) {
    return String(explicit).trim().toLowerCase();
  }
  return getPreviewVertical() || getLandingVertical();
}

/** First non-empty trimmed string among mixed preferred/selected facility shapes. */
function firstNonemptyString(...values: unknown[]): string | null {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return null;
}

function typeCandidatesFromFacility(source?: Record<string, any> | null): unknown[] {
  if (!source) return [];
  const details = source.facility_details;
  const targeting =
    details?.profile_targeting_json ?? source.profile_targeting_json;
  return [
    details?.facility_type,
    source.facility_type,
    source.facility_type_obj?.display_name,
    details?.facility_type_obj?.display_name,
    source.type_label,
    details?.type_label,
    details?.primary_profile_slug,
    source.primary_profile_slug,
    targeting?.primary_profile_slug,
  ];
}

function taxonomyDescCandidatesFromFacility(
  source?: Record<string, any> | null,
): unknown[] {
  if (!source) return [];
  const details = source.facility_details;
  const primary = source.primary_taxonomy;
  const detailsPrimary = details?.primary_taxonomy;
  return [
    details?.primary_taxonomy_desc,
    source.primary_taxonomy_desc,
    primary?.description,
    primary?.desc,
    detailsPrimary?.description,
    detailsPrimary?.desc,
  ];
}

/** Resolve checkout type across preferred / leftover / selected facility shapes. */
export function resolveCheckoutFacilityType(
  ...sources: Array<Record<string, any> | null | undefined>
): string | null {
  const snf = resolveSnfCheckoutOverride(...sources);
  if (snf) return snf.display_name;
  const rhc = resolveRhcFacilityTypeOverride(...sources);
  if (rhc) return rhc.display_name;
  // Do not prefer physician taxonomy over RHC/FQHC facility_type (5340 overreach).
  const resolved = firstNonemptyString(
    ...sources.flatMap((source) => typeCandidatesFromFacility(source)),
  );
  if (
    resolved &&
    isRhcfqhcOrGenericFacilityType(resolved) &&
    !/rhc|fqhc|rural\s*health|federally\s*qualified/i.test(resolved)
  ) {
    // Generic Facility/Clinic only — allow taxonomy desc (SNF already handled).
    return firstNonemptyString(
      ...sources.flatMap((source) => taxonomyDescCandidatesFromFacility(source)),
      resolved,
    );
  }
  if (!resolved) {
    return firstNonemptyString(
      ...sources.flatMap((source) => taxonomyDescCandidatesFromFacility(source)),
    );
  }
  return resolved;
}

/** Resolve numeric facility_type_id from preferred details or the selected NPI card. */
export function resolveCheckoutFacilityTypeId(
  ...sources: Array<Record<string, any> | null | undefined>
): number | null {
  const storedType = firstNonemptyString(
    ...sources.flatMap((source) => typeCandidatesFromFacility(source)),
  );
  const snf = resolveSnfCheckoutOverride(...sources);
  if (snf && isRhcfqhcOrGenericFacilityType(storedType)) {
    return null;
  }
  for (const source of sources) {
    if (!source) continue;
    const id = toFiniteNumber(
      source.facility_type_id ??
        source.facility_details?.facility_type_id ??
        source.facility_type_obj?.id ??
        source.facility_details?.facility_type_obj?.id,
    );
    if (id != null) return id;
  }
  return null;
}

/** Profile slug for pricing tiers. SNF leftover must not keep an RHC slug. */
export function resolveCheckoutProfileSlug(
  ...sources: Array<Record<string, any> | null | undefined>
): string | null {
  const snf = resolveSnfCheckoutOverride(...sources);
  if (snf) return snf.slug;
  const rhc = resolveRhcFacilityTypeOverride(...sources);
  if (rhc) return rhc.slug;
  return firstNonemptyString(
    ...sources.flatMap((source) => {
      if (!source) return [];
      const details = source.facility_details;
      const targeting =
        details?.profile_targeting_json ?? source.profile_targeting_json;
      return [
        source.primary_profile_slug,
        details?.primary_profile_slug,
        targeting?.primary_profile_slug,
        source.facility_type_slug,
        details?.facility_type_slug,
      ];
    }),
  );
}

/** NUCC taxonomy on preferred details, leftover, or the selected NPI card. */
export function resolveCheckoutTaxonomy(
  ...sources: Array<Record<string, any> | null | undefined>
): { code: string | null; desc: string | null } {
  return {
    code: firstNonemptyString(
      ...sources.flatMap((source) => {
        if (!source) return [];
        const details = source.facility_details;
        const primary = source.primary_taxonomy;
        const detailsPrimary = details?.primary_taxonomy;
        return [
          details?.primary_taxonomy_code,
          source.primary_taxonomy_code,
          primary?.code,
          detailsPrimary?.code,
        ];
      }),
    ),
    desc: firstNonemptyString(
      ...sources.flatMap((source) => taxonomyDescCandidatesFromFacility(source)),
    ),
  };
}

export function readPreferredFacilityForPricing<T extends Record<string, unknown> = any>(
  routePreferredFacilityId?: number | null,
  verticalScope?: string | null,
): T | null {
  if (typeof window === "undefined") return null;

  try {
    const raw = sessionStorage.getItem(PREFERRED_FACILITY_FOR_PRICING_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as T;
    const routeId = toFiniteNumber(routePreferredFacilityId);
    const storedId = toFiniteNumber(parsed?.id);
    const storedFacilityId = toFiniteNumber(
      (parsed as { facility_id?: unknown }).facility_id,
    );
    const storedPreferredPk = toFiniteNumber(
      (parsed as { user_preferred_facility_id?: unknown })
        .user_preferred_facility_id,
    );

    // Update Payment passes leftover rules facility_id; after admin-promo
    // hydrate persist.id is the user-preferred PK. Either must keep the blob.
    if (
      routeId != null &&
      storedId !== routeId &&
      storedFacilityId !== routeId &&
      storedPreferredPk !== routeId
    ) {
      sessionStorage.removeItem(PREFERRED_FACILITY_FOR_PRICING_KEY);
      return null;
    }

    const scope = resolvePreferredPricingVerticalScope(verticalScope);
    if (scope) {
      const got = storedPreferredFacilityVertical(
        parsed as Record<string, unknown>,
      );
      if (got !== scope) {
        sessionStorage.removeItem(PREFERRED_FACILITY_FOR_PRICING_KEY);
        return null;
      }
    }

    return parsed;
  } catch {
    sessionStorage.removeItem(PREFERRED_FACILITY_FOR_PRICING_KEY);
    return null;
  }
}

export function writePreferredFacilityForPricing(value: Record<string, unknown>): void {
  if (typeof window === "undefined") return;
  sessionStorage.setItem(PREFERRED_FACILITY_FOR_PRICING_KEY, JSON.stringify(value));
}
